import clsx from 'clsx'
import {compact, first, isEmpty, last, omit} from 'lodash'
import React, {useCallback, useMemo, useState} from 'react'
import {OverlayTrigger, Popover} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {DEFAULT_PAGE, FORMAT_DATE} from '../../../../../_gori/constants'
import {convertUserTimeZone, KTSVG} from '../../../../../_gori/helpers'
import {useA1BatchesProvider} from '../../../../../_gori/providers/A1BatchesProvider'
import {useAuth} from '../../../auth'
import A1_CONSTANTS from '../../core/_const'
import {A1Helpers} from '../../core/helpers'
import {useA1JourneyStatus} from '../../hooks'
import moment from 'moment'
import {CUSTOM_ISSUES_TAB_KEY} from '../../consts'
import {useSearchParams} from 'react-router-dom'

type DataRowProps = {
  data: {
    label?: string
    value?: any
    children?: any
  }
}

const StatusRow = ({label}) => (
  <div className='col-2 min-w-100px'>
    <div className='px-2'>{label}</div>
  </div>
)

const DataRow: React.FC<DataRowProps> = ({data: {value = [], children = null}}) => (
  <div className='col-2 min-w-100px'>
    {children ||
      value.map(({date, time, decoration = false}, idx) => (
        <div className={clsx('px-2 fw-bold', {'mt-2': idx !== 0})} key={idx}>
          {date && (
            <span className={clsx({'text-decoration-line-through': decoration})}>{date}</span>
          )}
          {time && (
            <div
              className={clsx('mt-1', {
                'text-gray-400 text-decoration-line-through': decoration,
                'text-gray-600': !decoration,
              })}
            >
              {time}
            </div>
          )}
        </div>
      ))}
  </div>
)

const JourneyStatusCard: React.FC = () => {
  const intl = useIntl()
  const {a1Batch} = useA1BatchesProvider()
  const {currentUser} = useAuth()
  const [showPopover, setShowPopover] = useState<boolean>(false)
  const {journeyStatusLargerOrEqualCustoms, holdRejectedCount} = useA1JourneyStatus()
  const [, setSearchParams] = useSearchParams()

  const STATUS_WITHOUT_NA = Object.entries(omit(A1_CONSTANTS.OPTIONS_JOURNEY_STATUS, 'N_A'))

  const onClickHoldRejected = useCallback(() => {
    const params = new URLSearchParams()

    params.set('page', DEFAULT_PAGE)
    params.set('order_tab', CUSTOM_ISSUES_TAB_KEY)

    setSearchParams(params)
  }, [setSearchParams])

  const customDateTime = useCallback(
    (date, format = FORMAT_DATE.DATE_TIME) => {
      if (isEmpty(date)) return {date: null, time: null}
      const dateTime = convertUserTimeZone(date, currentUser, format).split(' ')
      return {date: dateTime[0], time: dateTime.slice(1).join(' ') || null}
    },
    [currentUser]
  )

  const customLocalDateTime = useCallback(
    (date, format = FORMAT_DATE.DATE_TIME_TIMEZONE_EXCLUDE_SECOND) => {
      if (isEmpty(date)) return {date: null, time: null}
      const dateTime = moment(date).local().format(format).split(' ')
      return {date: dateTime[0], time: dateTime.slice(1).join(' ') || null}
    },
    []
  )

  const flightInFo: any = useMemo(
    () => ({
      origin: first(a1Batch?.flights),
      destination: last(a1Batch?.flights),
    }),
    [a1Batch?.flights]
  )

  const pickupAddress = useMemo(() => {
    const {street1, city, state, zip, country} = a1Batch?.pickup_address || {}
    return {
      short: compact([city, country]).join(', '),
      full: compact([street1, city, state, zip, country]).join(', '),
    }
  }, [a1Batch?.pickup_address])

  const pickupModal = useMemo(
    () => (
      <OverlayTrigger
        placement='left'
        show={showPopover}
        overlay={
          <Popover
            className='mw-500px'
            onMouseEnter={() => setShowPopover(true)}
            onMouseLeave={() => setShowPopover(false)}
          >
            <div className='p-5 rounded-2 shadow fw-bold'>{pickupAddress.full}</div>
          </Popover>
        }
      >
        <div
          className='text-primary text-decoration-underline cursor-pointer'
          onMouseEnter={() => setShowPopover(true)}
          onMouseLeave={() => setShowPopover(false)}
        >
          {pickupAddress.short}
        </div>
      </OverlayTrigger>
    ),
    [pickupAddress, showPopover]
  )

  const delivery = useMemo(
    () =>
      A1Helpers.checkJourneyStatus({
        statusCurrent: a1Batch?.journey_status,
        statusCheck: A1_CONSTANTS.OPTIONS_JOURNEY_STATUS.DELIVERY_STATUS.value[0] || '',
      }),
    [a1Batch?.journey_status]
  )

  const deliveryLabel = useMemo(() => {
    const trackingStatus = a1Batch?.tracking_status || {}
    const carriers = {
      USPS: {name: 'USPS', value: Number(trackingStatus.usps)},
      UPS: {name: 'UPS', value: Number(trackingStatus.ups)},
      FEDEX: {name: 'FedEx', value: trackingStatus.fedex},
    }

    const formatStatus = (name: string, value: number | undefined) => {
      if (Number.isNaN(value)) return null
      return (
        <div className='text-info text-start mb-2' key={name}>
          {name}:{' '}
          {value === 100 ? (
            <span className='text-nowrap'>
              {intl.formatMessage({id: 'DELIVERED'})}
              <KTSVG path='/media/gori/a1/verify.svg' className='svg-icon-2 text-primary ms-1' />
            </span>
          ) : (
            `${value}%`
          )}
        </div>
      )
    }

    return (
      <div>
        {carriers.USPS.value === 100 && carriers.UPS.value === 100
          ? formatStatus(`${carriers.USPS.name}/${carriers.UPS.name}`, 100)
          : Object.values(omit(carriers, 'FEDEX')).map((carrier) =>
              formatStatus(carrier.name, carrier.value)
            )}
        {!isEmpty(carriers.FEDEX.value) && (
          <div className='text-info text-start mb-2'>
            {carriers.FEDEX.name}: {carriers.FEDEX.value}
          </div>
        )}
      </div>
    )
  }, [a1Batch?.tracking_status, intl])

  const estimatedPickup = customLocalDateTime(a1Batch?.estimated_pickup_at)
  const actualPickup = customLocalDateTime(a1Batch?.actual_pickup_at)

  const dataRowsFirst = [
    {
      label: a1Batch?.orders_done ? intl.formatMessage({id: 'LABEL_CREATED'}) : null,
      value: [customDateTime(a1Batch?.readied_at)],
    },
    {
      label:
        a1Batch?.estimated_pickup_at || a1Batch?.actual_pickup_at
          ? intl.formatMessage({id: 'PICK_UP_DATE'})
          : null,
      value: [
        customLocalDateTime(a1Batch?.actual_pickup_at),
        a1Batch?.actual_pickup_at
          ? estimatedPickup?.date !== actualPickup?.date ||
            estimatedPickup?.time !== actualPickup?.time
            ? {...estimatedPickup, decoration: true}
            : null
          : {...estimatedPickup},
      ]?.filter(Boolean),
    },
    {
      label:
        flightInFo?.origin?.estimated_departure_at || flightInFo?.destination?.estimated_arrival_at
          ? `${intl.formatMessage({id: 'ETD'})}/${intl.formatMessage({id: 'ETA'})}`
          : null,
      value: [
        customDateTime(flightInFo?.origin?.estimated_departure_at),
        customDateTime(flightInFo?.destination?.estimated_arrival_at),
      ],
    },
    {
      label:
        journeyStatusLargerOrEqualCustoms && a1Batch?.released_at
          ? intl.formatMessage({id: 'RELEASED'})
          : null,
      value: [journeyStatusLargerOrEqualCustoms && customDateTime(a1Batch?.released_at)],
    },
    {label: null, value: [customDateTime(a1Batch?.dropped_off_at)]},
    {
      children: delivery && deliveryLabel,
    },
  ]

  const dataRowsSecond = [
    {label: null, value: [{date: null, time: null}]},
    {
      label: !!pickupAddress.short ? intl.formatMessage({id: 'PICK_UP_ADDRESS'}) : null,
      children: !!pickupAddress.short && pickupModal,
    },
    {
      label:
        flightInFo?.origin?.actual_departure_at || flightInFo?.destination?.actual_arrival_at
          ? `${intl.formatMessage({id: 'ATD'})}/${intl.formatMessage({id: 'ATA'})}`
          : null,
      value: [
        customDateTime(flightInFo?.origin?.actual_departure_at),
        customDateTime(flightInFo?.destination?.actual_arrival_at),
      ],
    },
    {
      label: holdRejectedCount ? intl.formatMessage({id: 'HOLD_REJECTED'}) : null,
      children: (
        <div className='px-2 fw-bold'>
          {!!holdRejectedCount && (
            <div
              className={clsx('rounded-pill cursor-pointer fw-bolder', {
                'bg-light-danger p-2': a1Batch?.issue_resolved !== true,
                'bg-gray-300 p-1': a1Batch?.issue_resolved === true,
              })}
              onClick={onClickHoldRejected}
            >
              {intl.formatMessage({
                id: a1Batch?.issue_resolved === true ? 'ISSUES_RESOLVED' : 'CUSTOM_ISSUES',
              })}{' '}
              ({holdRejectedCount})
            </div>
          )}
        </div>
      ),
    },
    {label: null, value: [{date: null, time: null}]},
    {label: null, value: [{date: null, time: null}]},
  ]

  const statusColor = useCallback(
    (status) => {
      if (a1Batch?.journey_status === A1_CONSTANTS.OPTIONS_JOURNEY_STATUS.CUSTOMS.value[0]) {
        if (status.value.includes(a1Batch?.journey_status)) {
          return a1Batch?.issue_resolved ? status?.color : '#FF7272'
        }
        return ''
      }
      return status.value.includes(a1Batch?.journey_status) ? status?.color : ''
    },
    [a1Batch?.issue_resolved, a1Batch?.journey_status]
  )

  return (
    <div className='card mt-4 mt-xxl-0 ms-xxl-4 h-100'>
      <div className='card-header border-0 d-flex justify-content-between align-items-center fw-bolder'>
        <span className='fs-3'>{intl.formatMessage({id: 'JOURNEY_STATUS'})}</span>
      </div>
      <div className='card-body d-flex flex-column scroll-x pb-6'>
        <div className='d-flex text-center mb-6'>
          {Object.entries(omit(A1_CONSTANTS.OPTIONS_JOURNEY_STATUS, 'N_A')).map(([key, status]) => (
            <div className='col-2 min-w-100px position-relative' key={key}>
              <div className='w-100 border border-2 border-secondary position-absolute top-50 start-50 translate-middle' />
              <KTSVG
                path={`/media/gori/a1/${
                  status.value.includes(a1Batch?.journey_status)
                    ? 'checkbox_checked'
                    : 'checkbox_unchecked'
                }.svg`}
                style={{
                  color: statusColor(status),
                }}
                className='svg-icon-1 position-absolute top-50 start-50 translate-middle'
              />
            </div>
          ))}
        </div>
        <div className='d-flex text-center fs-5 fs-xxl-7 fw-bolder mb-4'>
          {STATUS_WITHOUT_NA.map(([key, status]) => (
            <StatusRow label={intl.formatMessage({id: status.label})} key={key} />
          ))}
        </div>
        <div className='d-flex text-center fs-6 fs-xxl-8 fw-bolder mb-2'>
          {dataRowsFirst.map(({label}, idx) => (
            <StatusRow label={label} key={idx} />
          ))}
        </div>
        <div className='d-flex text-center fs-6 fs-xxl-8 fw-bolder mb-4'>
          {dataRowsFirst.map((data: {label?: any; value?: any; children?: any}, idx) => (
            <DataRow data={data} key={idx} />
          ))}
        </div>
        <div className='d-flex text-center fs-6 fs-xxl-8 fw-bolder mb-2'>
          {dataRowsSecond.map(({label}, idx) => (
            <StatusRow label={label} key={idx} />
          ))}
        </div>
        <div className='d-flex text-center fs-6 fs-xxl-8 fw-bolder'>
          {dataRowsSecond.map((data: {label?: any; value?: any; children?: any}, idx) => (
            <DataRow data={data} key={idx} />
          ))}
        </div>
      </div>
    </div>
  )
}

export {JourneyStatusCard}
